import { FC } from 'react'
import Modal from '@common/Modal'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import s from './index.module.css'

/** МОП — доска CRM + лиды. */
export const SALES_MANAGER_ROLE = 'sales-manager' as const
/** РОП — админ проекта «Отдел продаж» в трекере. */
export const HEAD_OF_SALES_ROLE = 'head-of-sales' as const

export type CrmSalesConsequenceRole =
  | typeof SALES_MANAGER_ROLE
  | typeof HEAD_OF_SALES_ROLE

export type CrmSalesConsequenceMode = 'dismiss' | 'remove-role'

export const CRM_SALES_CONSEQUENCE_ROLES: readonly CrmSalesConsequenceRole[] = [
  SALES_MANAGER_ROLE,
  HEAD_OF_SALES_ROLE,
]

const ROLE_LABELS: Record<CrmSalesConsequenceRole, string> = {
  [SALES_MANAGER_ROLE]: 'Менеджер отдела продаж',
  [HEAD_OF_SALES_ROLE]: 'Руководитель отдела продаж',
}

type RoleLike = { item_name?: string | null }

export const isCrmSalesConsequenceRole = (
  roleName: string | null | undefined,
): roleName is CrmSalesConsequenceRole =>
  CRM_SALES_CONSEQUENCE_ROLES.includes(roleName as CrmSalesConsequenceRole)

export const isHeadOfSalesRole = (
  roleName: string | null | undefined,
): roleName is typeof HEAD_OF_SALES_ROLE => roleName === HEAD_OF_SALES_ROLE

export const isSalesManagerRole = (
  roleName: string | null | undefined,
): roleName is typeof SALES_MANAGER_ROLE => roleName === SALES_MANAGER_ROLE

export const userHasSalesManagerRole = (roles: RoleLike[] | null | undefined) =>
  (roles ?? []).some((role) => isSalesManagerRole(role?.item_name))

export const userHasHeadOfSalesRole = (roles: RoleLike[] | null | undefined) =>
  (roles ?? []).some((role) => isHeadOfSalesRole(role?.item_name))

export const userHasCrmSalesConsequenceRole = (
  roles: RoleLike[] | null | undefined,
) => userHasSalesManagerRole(roles) || userHasHeadOfSalesRole(roles)

const MOP_ITEMS = [
  'Будет удалена только CRM-доска этого сотрудника в трекере задач. Остальные доски не затрагиваются. Если сотруднику нужен трекер для других задач — добавьте его туда вручную после этого действия.',
  'Заявки, закреплённые за этим менеджером, без переназначения окажутся в «В работе». Рекомендуем заранее перераспределить их другим менеджерам.',
]

const ROP_WARN_ITEMS = [
  'Сотрудник перестанет быть администратором проекта отдела продаж в трекере задач.',
  'Доступ к настройкам этого проекта у него пропадёт. При необходимости заранее назначьте другого администратора.',
]

type ModalCopy = {
  title: string
  confirmLabel: string | null
  lead: string
  items: string[]
}

const getCopy = (
  mode: CrmSalesConsequenceMode,
  roleKinds: CrmSalesConsequenceRole[],
  blocked: boolean,
  projectTitle?: string,
): ModalCopy => {
  const hasRop = roleKinds.includes(HEAD_OF_SALES_ROLE)
  const hasMop = roleKinds.includes(SALES_MANAGER_ROLE)
  const singleLabel =
    roleKinds.length === 1 ? ROLE_LABELS[roleKinds[0]] : null

  if (hasRop && blocked) {
    return {
      title:
        mode === 'remove-role' && singleLabel
          ? `Нельзя удалить роль «${singleLabel}»`
          : 'Нельзя уволить сотрудника',
      confirmLabel: null,
      lead: projectTitle
        ? `В проекте «${projectTitle}» этот сотрудник — единственный администратор.`
        : 'Этот сотрудник — единственный администратор проекта отдела продаж в трекере задач.',
      items: [
        'После снятия роли или увольнения в проекте не останется администратора — так делать нельзя.',
        'Сначала назначьте другого администратора проекта в трекере задач, затем повторите действие.',
      ],
    }
  }

  const items = [
    ...(hasRop ? ROP_WARN_ITEMS : []),
    ...(hasMop ? MOP_ITEMS : []),
  ]

  return {
    title:
      mode === 'remove-role' && singleLabel
        ? `Удалить роль «${singleLabel}»?`
        : 'Уволить сотрудника?',
    confirmLabel: mode === 'remove-role' ? 'Удалить роль' : 'Уволить',
    lead: 'Перед подтверждением учтите:',
    items: items.length > 0 ? items : MOP_ITEMS,
  }
}

export type CrmSalesManagerConsequencesModalProps = {
  open: boolean
  mode?: CrmSalesConsequenceMode
  roleKinds?: CrmSalesConsequenceRole[]
  blocked?: boolean
  projectTitle?: string
  checking?: boolean
  loading?: boolean
  onConfirm: () => void
  onDismiss: () => void
}

/** Пояснения перед увольнением / снятием роли МОП или РОП. */
const CrmSalesManagerConsequencesModal: FC<
  CrmSalesManagerConsequencesModalProps
> = ({
  open,
  mode = 'dismiss',
  roleKinds = [SALES_MANAGER_ROLE],
  blocked = false,
  projectTitle,
  checking = false,
  loading = false,
  onConfirm,
  onDismiss,
}) => {
  const { title, confirmLabel, lead, items } = getCopy(
    mode,
    roleKinds,
    blocked,
    projectTitle,
  )
  const busy = loading || checking

  return (
    <Modal
      active={open}
      className={mclasses.confirmModalWrapper as never}
      setActive={() => {
        if (busy) return
        onDismiss()
      }}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>{title}</span>
          </div>
          <button
            type="button"
            className={mclasses.closeIcon}
            onClick={onDismiss}
            aria-label="Закрыть"
            disabled={busy}
          >
            <IcoRemove />
          </button>
        </div>
        <div className={`${mclasses.formWrapper} ${s.body}`}>
          {checking ? (
            <p className={s.lead}>Проверяем права администратора проекта…</p>
          ) : (
            <>
              <p className={s.lead}>{lead}</p>
              <ul className={s.list}>
                {items.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={onDismiss} disabled={busy}>
            {blocked && !checking ? 'Понятно' : 'Отмена'}
          </Button>
          {!blocked && !checking && confirmLabel ? (
            <Button
              color="danger"
              onClick={onConfirm}
              isLoading={loading}
              disabled={busy}
            >
              {confirmLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export default CrmSalesManagerConsequencesModal
